import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldAlert } from "lucide-react";
import { useContentProtection } from "../../contexts/ContentProtectionContext";
import useCopyGuard from "../../hooks/useCopyGuard";
import useDevToolsGuard from "../../hooks/useDevToolsGuard";

/**
 * ContentShield — enforces the institute's LMS-wide content protection for a
 * signed-in STUDENT. Mounted once, beside the routes. Renders nothing unless
 * there is something to say.
 *
 *  • Copy block: selection, copy, cut, drag and right-click stop everywhere
 *    except the student's own fields (useCopyGuard), with a short, rate-limited
 *    hint so a blocked Ctrl+C reads as a rule rather than a broken page.
 *  • Developer tools: the shortcuts and the context menu are closed, and while
 *    the panel is detected open the whole app is hidden behind a notice and
 *    every playing video or audio is paused. Closing the panel restores it.
 *
 * `policy.applies` comes from the server, which only sets it for students.
 */
const HINT_MS = 2600;
const HINT_WORDS = {
  copy: "Copying is turned off by your institute.",
  cut: "Copying is turned off by your institute.",
  drag: "Copying is turned off by your institute.",
  menu: "Right-click is turned off by your institute.",
  save: "Saving pages is turned off by your institute.",
  print: "Printing is turned off by your institute.",
};

export default function ContentShield() {
  const { policy } = useContentProtection();
  const [hint, setHint] = useState(null);
  const hintTimer = useRef(null);

  const onBlocked = useCallback((action) => {
    const text = HINT_WORDS[action];
    if (!text) return;
    setHint(text);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setHint(null), HINT_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(hintTimer.current), []);

  const blockCopy = policy.applies && policy.blockCopy;
  useCopyGuard({ active: blockCopy, onBlocked });
  const devtoolsOpen = useDevToolsGuard({ active: policy.applies && policy.blockDevTools });

  // An in-app "Copy" button (an AI answer, a code snippet) writes through the
  // Clipboard API, which fires no `copy` event — so the listener above never
  // sees it. While copying is blocked, that door is closed too.
  useEffect(() => {
    const clipboard = navigator.clipboard;
    if (!blockCopy || !clipboard) return undefined;
    const refuse = () => {
      onBlocked("copy");
      return Promise.reject(new DOMException("Copying is turned off by your institute.", "NotAllowedError"));
    };
    // Own properties shadowing Clipboard.prototype; deleting them restores it.
    try {
      clipboard.writeText = refuse;
      clipboard.write = refuse;
    } catch { /* a locked-down Clipboard object — the copy listener still applies */ }
    return () => {
      try {
        delete clipboard.writeText;
        delete clipboard.write;
      } catch { /* see above */ }
    };
  }, [blockCopy, onBlocked]);

  useEffect(() => {
    if (!devtoolsOpen) return undefined;
    const root = document.documentElement;
    root.classList.add("cp-shielded");
    document.querySelectorAll("video, audio").forEach((media) => {
      try { media.pause(); } catch { /* detached element */ }
    });
    return () => root.classList.remove("cp-shielded");
  }, [devtoolsOpen]);

  return (
    <>
      {devtoolsOpen && createPortal(
        <div
          className="cp-shield tw-fixed tw-inset-0 tw-z-critical tw-flex tw-items-center tw-justify-center tw-bg-surface tw-p-6"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="cp-shield-title"
          aria-describedby="cp-shield-body"
        >
          <div className="tw-flex tw-max-w-md tw-flex-col tw-items-center tw-gap-3 tw-text-center">
            <ShieldAlert size={40} className="tw-text-warning" aria-hidden="true" />
            <h2 id="cp-shield-title" className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">
              Close developer tools to continue
            </h2>
            <p id="cp-shield-body" className="tw-m-0 tw-text-sm tw-text-content-muted">
              Your institute protects its course content, so the LMS is hidden while
              developer tools are open. Close them and this page comes straight back.
            </p>
          </div>
        </div>,
        document.body
      )}
      {hint && (
        <div
          className="tw-pointer-events-none tw-fixed tw-bottom-6 tw-left-1/2 tw-z-toast tw--translate-x-1/2 tw-rounded-full tw-bg-surface-dark tw-px-4 tw-py-2 tw-text-sm tw-text-on-surface-dark tw-shadow-popover"
          role="status"
        >
          {hint}
        </div>
      )}
    </>
  );
}
